import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Download, X } from 'lucide-react';
import { Button } from './ui/button';

const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      
      // Only show prompt if user hasn't dismissed it before
      const dismissed = localStorage.getItem('pwaInstallDismissed');
      if (!dismissed) {
        setTimeout(() => setShowPrompt(true), 3000);
      }
    };

    window.addEventListener('beforeinstallprompt', handler);

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      console.log('User accepted the install prompt');
    }

    setDeferredPrompt(null);
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwaInstallDismissed', 'true');
  };

  if (!showPrompt) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        className="fixed bottom-20 md:bottom-24 left-4 right-4 md:left-auto md:right-8 md:w-96 z-40"
        data-testid="pwa-install-prompt"
      >
        <div className="bg-[#00E5FF] border-4 border-black rounded-2xl p-5 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]">
          <button
            onClick={handleDismiss}
            className="absolute top-2 right-2 text-black hover:bg-black/10 rounded-full p-1 transition-colors"
            data-testid="dismiss-install-button"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="flex items-start gap-3 mb-3">
            <Download className="w-8 h-8 text-black flex-shrink-0" strokeWidth={3} />
            <div>
              <h3 className="text-xl font-black text-black uppercase tracking-tight" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                Add to Home Screen
              </h3>
              <p className="text-black text-sm font-medium leading-relaxed mt-1" style={{ fontFamily: 'Figtree, sans-serif' }}>
                Install Pathways for quick access, offline support, and a better experience!
              </p>
            </div>
          </div>

          <div className="flex gap-3">
            <Button
              onClick={handleDismiss}
              className="flex-1 bg-white text-black font-bold py-2.5 rounded-xl border-2 border-black hover:bg-gray-100 transition-colors"
              style={{ fontFamily: 'Bebas Neue, sans-serif' }}
            >
              Maybe Later
            </Button>
            <Button
              onClick={handleInstall}
              className="flex-1 bg-black text-[#00E5FF] font-black py-2.5 rounded-xl hover:bg-black/90 shadow-[4px_4px_0px_0px_rgba(0,0,0,0.3)] transition-all uppercase tracking-wide"
              style={{ fontFamily: 'Bebas Neue, sans-serif' }}
              data-testid="install-app-button"
            >
              Install App
            </Button>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default PWAInstallPrompt;
